export const submissionSettingsQuery = /* groq */ `
  *[_type == "submissionSettings"][0]{
    terms[]{
      "key": key.current,
      label,
      type,
      required,
      content
    },
    sections[]{
      "key": key.current,
      title,
      description,
      fields[]{
        "key": key.current,
        label,
        type,
        required,
        placeholder,
        validation,
        options[]{ label, value },
        showResultPage,
        fields[]{
          "key": key.current,
          label, type, required, placeholder, validation,
          options[]{ label, value },
          showResultPage
        }
      }
    }
  }
`
import { defineQuery } from 'next-sanity'

export const homePageQuery = defineQuery(`
  {
    "home": *[_type == "home" && !(_id in path('drafts.**'))][0]{
      _id,
      _type,
      title,
      slides[]{
        image,
        mobileImage,
        spotlight,
        title,
        category,
        link,
        date
      }
    },
    "recentNotices": *[_type == "notice" && !(_id in path('drafts.**')) && publishedAt <= now()] | order(publishedAt desc, _createdAt desc)[0...5]{
      _id,
      title,
      publishedAt,
      "slug": slug
    },
    "recentPress": *[_type == "press" && !(_id in path('drafts.**')) && publishedAt <= now()] | order(publishedAt desc, _createdAt desc)[0...5]{
      _id,
      title,
      publishedAt,
      "slug": slug,
      category
    },
    "currentYearSchedule": *[_type == "screening" && !(_id in path('drafts.**')) && publishedAt <= now() && years._ref == *[_type == "settings"][0].years._ref] | order(publishedAt desc){
      _id,
      _type,
      title,
      "year": years->year,
      "places": places->{
        title,
        "slug": slug.current,
      },
      "place": place,
      "round": round,
      "duration": duration,
      "programs": programs[]->{
        _id,
        title,
        title_kr,
        "slug": slug.current,
        "subtitles": subtitles,
        "rating": rating,
        subtitles,
      },
      screeningEvents[]->{
        symbol,
        _id
      },
      "slug": slug.current,
    },
    "msffSlogan": *[_type == "msff" && !(_id in path('drafts.**')) && years._ref == *[_type == "settings"][0].years._ref][0]{
      slogan
    },
    "randomPrograms": *[_type == "programs" && !(_id in path('drafts.**')) && publishedAt <= now()] | order(year desc, publishedAt desc)[0...50]{
      _id,
      title,
      title_kr,
      thumbnail,
      "slug": slug.current,
      year,
      genre,
      section[]->{
        _id,
        title,
        "slug": slug.current,
      },
      director[]->{
        _id,
        title,
        title_kr,
      },
      color,
      subtitles,
      rating,
      runtime,
      premiere
    },
    "currentYearSponsors": *[_type == "sponsors" && !(_id in path('drafts.**')) && years._ref == *[_type == "settings"][0].years._ref][0]{
      _id,
      sponsors[]->{
        _id,
        title,
        thumbnail,
        url
      },
      supporters_premiere[]->{
        _id,
        title,
        thumbnail,
        url
      },
      supporters_festival[]->{
        _id,
        title,
        thumbnail,
        url
      },
      supporters_partner[]->{
        _id,
        title,
        thumbnail,
        url
      },
      supporters_major[]->{
        _id,
        title,
        thumbnail,
        url
      },
      supporters_collaborator[]->{
        _id,
        title,
        thumbnail,
        url
      },
    }
  }
`)

export const randomProgramsQuery = defineQuery(`
  *[_type == "programs" && !(_id in path('drafts.**'))] | order(_createdAt desc)[0...50]{
    _id,
    title,
    description,
    image,
    "slug": slug
  }
`)

export const pagesBySlugQuery = defineQuery(`
  *[_type == "page" && slug.current == $slug][0] {
    _id,
    _type,
    body,
    overview,
    title,
    "slug": slug.current,
  }
`)

export const projectBySlugQuery = defineQuery(`
  *[_type == "project" && slug.current == $slug][0] {
    _id,
    _type,
    client,
    coverImage,
    description,
    duration,
    overview,
    site,
    "slug": slug.current,
    tags,
    title,
  }
`)

export const eventsQuery = defineQuery(`
  *[_type == "events" && publishedAt <= now() && !(_id in path('drafts.**'))] | order(publishedAt asc) {
    _id,
    _type,
    title,
    description,
    slug,
    year->{
      _id,
      title
    },
    contents[] {
      title,
      description
    }
  }
`)

export const settingsQuery = defineQuery(`
  *[_type == "settings"][0]{
    _id,
    _type,
    menuItems[active == true]{
      ...,
      subMenu[defined(_ref) || active == true]{
        defined(_ref) => {
          ...@->{
            _type,
            title
          }
        },
        !defined(_ref) => {
          ...,
          subMenu[defined(_ref) || active == true]{
            defined(_ref) => {
              ...@->{
                _type,
                title
              }
            },
            !defined(_ref) => {
              ...
            }
          }
        }
      }
    },
    ogImage,
  }
`)

export const footerQuery = defineQuery(`
  *[_type == "settings"][0]{
    _id,
    _type,
    footer,
    footerLogo,
    sns,
    menuItems[active == true]{
      ...,
      subMenu[defined(_ref) || active == true]{
        defined(_ref) => {
          ...@->{
            _type,
            title
          }
        },
        !defined(_ref) => {
          ...,
          subMenu[defined(_ref) || active == true]{
            defined(_ref) => {
              ...@->{
                _type,
                title
              }
            },
            !defined(_ref) => {
              ...
            }
          }
        }
      }
    }
  }
`)
export const nowYearQuery = defineQuery(`
  *[_type == "settings"][0].years->{
    year,
    festival_round,
    duration{
      start,
      end
    }
  }
`)

export const msffQuery = defineQuery(`
  *[_type == "msff" && years->.year == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
    _id,
    _type,
    cooperation,
    host,
    organizer,
    place,
    posterDescription,
    posterDesign,
    slogan,
    sponsor,
    title,
    trailerDescription,
    trailerProduction,
    duration,
    years->{
      duration,
      year
    },
    poster,
    trailer,
    introduction
  }
`)

export const currentMsffQuery = defineQuery(`
  *[_type == "msff" && years._ref == *[_type == "settings"][0].years._ref && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
    _id,
    _type,
    cooperation,
    host,
    organizer,
    place,
    posterDescription,
    posterDesign,
    slogan,
    sponsor,
    title,
    trailerDescription,
    trailerProduction,
    duration,
    years->{
      duration,
      year
    },
    poster,
    trailer,
    introduction
  }
`)

export const popupQuery = defineQuery(`
  *[_type == "popup" && publishedAt <= now() && (!showEndTime || endTime >= now())]{
    _id,
    _type,
    title,
    actionName,
    actionUrl,
    publishedAt,
    showEndTime,
    endTime
  }
`)

export const awardJuryQuery = defineQuery(`
  *[_type == "award" && !(_id in path('drafts.**')) && publishedAt <= now() && years->.year == *[_type == "settings"][0].years->year][0]{
    _id,
    _type,
    award,
    jury_chairman{
      director->{
        _id,
        title,
        title_kr,
        job,
        thumbnail,
      },
      description,
    },
    jury[]{
      _key,
      section->{
        title,
        "slug": slug.current,
        contents,
        logo
      },
      director->{
        _key,
        title,
        title_kr,
        job,
        thumbnail,
        "slug": slug.current,
      },
      description,
    }
  }
`)

export const staffQuery = defineQuery(`
  *[_type == "staff" && years->.year == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
    _id,
    _type,
    years->{
      year
    },
    administrators[]{
      name,
      job,
      description,
    },
    executives[]{
      name,
      job,
      description,
    },
    staffs[]{
      name,
      job,
      description,
    }
  }
`)

export const currentStaffQuery = defineQuery(`
  *[_type == "staff" && years._ref == *[_type == "settings"][0].years._ref && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
    _id,
    _type,
    years->{
      year
    },
    administrators[]{
      name,
      job,
      description,
    },
    executives[]{
      name,
      job,
      description,
    },
    staffs[]{
      name,
      job,
      description,
    }
  }
`)

export const sponsorsQuery = defineQuery(`
  *[_type == "sponsors" && years->.year == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
    _id,
    _type,
    years->{
      year
    },
    sponsors[]{
      business->{
        _id,
        title,
        thumbnail,
        url
      }
    },
    supporters_premiere[]{
      business->{
        _id,
        title,
        thumbnail,
        url
      }
    },
    supporters_festival[]{
      business->{
        _id,
        title,
        thumbnail,
        url
      }
    },
    supporters_partner[]{
      business->{
        _id,
        title,
        thumbnail,
        url
      }
    },
    supporters_major[]{
      business->{
        _id,
        title,
        thumbnail,
        url
      }
    }
  }
`)

export const currentSponsorsQuery = defineQuery(`
  *[_type == "sponsors" && years._ref == *[_type == "settings"][0].years._ref][0]{
    _id,
    _type,
    years->{
      year
    },
    sponsors[]->{
      _id,
      title,
      thumbnail,
      url
    },
    supporters_premiere[]->{
      _id,
      title,
      thumbnail,
      url
    },
    supporters_festival[]->{
      _id,
      title,
      thumbnail,
      url
    },
    supporters_partner[]->{
      _id,
      title,
      thumbnail,
      url
    },
    supporters_major[]->{
      _id,
      title,
      thumbnail,
      url
    },
    supporters_collaborator[]->{
      _id,
      title,
      thumbnail,
      url
    }
  }
`)

export const noticeListQuery = defineQuery(`
  {
    "pinned": *[_type == "notice" && !(_id in path('drafts.**')) && publishedAt <= now() && pinned == true] | order(publishedAt desc, _createdAt desc){
      _id,
      _type,
      title,
      pinned,
      publishedAt,
      _createdAt,
      "slug": slug,
    },
    "posts": *[_type == "notice" && !(_id in path('drafts.**')) && publishedAt <= now() && pinned != true] | order(publishedAt desc, _createdAt desc)[$start...$end]{
      _id,
      _type,
      title,
      pinned,
      publishedAt,
      _createdAt,
      "slug": slug,
    },
    "total": count(*[_type == "notice" && !(_id in path('drafts.**')) && publishedAt <= now() && pinned != true])
  }
`)

export const pressListQuery = defineQuery(`
  {
    "pinned": *[_type == "press" && !(_id in path('drafts.**')) && publishedAt <= now() && pinned == true] | order(publishedAt desc, _createdAt desc){
      _id,
      _type,
      title,
      pinned,
      publishedAt,
      _createdAt,
      "slug": slug,
      category
    },
    "posts": *[_type == "press" && !(_id in path('drafts.**')) && publishedAt <= now() && pinned != true] | order(publishedAt desc, _createdAt desc)[$start...$end]{
      _id,
      _type,
      title,
      pinned,
      publishedAt,
      _createdAt,
      "slug": slug,
      category
    },
    "total": count(*[_type == "press" && !(_id in path('drafts.**')) && publishedAt <= now() && pinned != true])
  }
`)

// export const settingsQuery = defineQuery(`
//   *[_type == "settings"][0]{
//     _id,
//     _type,
//     footer,
//     menuItems[]{
//       _key,
//       ...@->{
//         _type,
//         "slug": slug.current,
//         title
//       }
//     },
//     ogImage,
//   }
// `)

export const noticeBySlugQuery = defineQuery(`
  {
    "notice": *[_type == "notice" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
      _id,
      _type,
      title,
      content,
      files[]{
        _key,
        asset->{
          _id,
          url,
          originalFilename,
          size
        }
      },
      publishedAt,
      _createdAt,
      "slug": slug,
    },
    "prev": *[_type == "notice" && !(_id in path('drafts.**')) && publishedAt <= now() && (publishedAt > *[_type == "notice" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0].publishedAt || (publishedAt == *[_type == "notice" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0].publishedAt && _createdAt > *[_type == "notice" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]._createdAt))] | order(publishedAt asc, _createdAt asc)[0]{
      _id,
      title,
      "slug": slug,
      publishedAt
    },
    "next": *[_type == "notice" && !(_id in path('drafts.**')) && publishedAt <= now() && (publishedAt < *[_type == "notice" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0].publishedAt || (publishedAt == *[_type == "notice" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0].publishedAt && _createdAt < *[_type == "notice" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]._createdAt))] | order(publishedAt desc, _createdAt desc)[0]{
      _id,
      title,
      "slug": slug,
      publishedAt
    }
  }
`)

export const pressBySlugQuery = defineQuery(`
  {
    "press": *[_type == "press" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
      _id,
      _type,
      title,
      content,
      files[]{
        _key,
        asset->{
          _id,
          url,
          originalFilename,
          size
        }
      },
      publishedAt,
      _createdAt,
      "slug": slug,
      category
    },
    "prev": *[_type == "press" && !(_id in path('drafts.**')) && publishedAt <= now() && (publishedAt > *[_type == "press" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0].publishedAt || (publishedAt == *[_type == "press" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0].publishedAt && _createdAt > *[_type == "press" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]._createdAt))] | order(publishedAt asc, _createdAt asc)[0]{
      _id,
      title,
      category,
      "slug": slug,
      publishedAt
    },
    "next": *[_type == "press" && !(_id in path('drafts.**')) && publishedAt <= now() && (publishedAt < *[_type == "press" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0].publishedAt || (publishedAt == *[_type == "press" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0].publishedAt && _createdAt < *[_type == "press" && slug == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]._createdAt))] | order(publishedAt desc, _createdAt desc)[0]{
      _id,
      title,
      category,
      "slug": slug,
      publishedAt
    }
  }
`)

export const allNoticesQuery = defineQuery(`
  *[_type == "notice" && !(_id in path('drafts.**')) && publishedAt <= now()]{
    _id,
    title,
    "slug": slug.current,
    publishedAt
  }
`)

export const slugsByTypeQuery = defineQuery(`
  *[_type == $type && defined(slug.current) && !(_id in path('drafts.**')) && publishedAt <= now()]{"slug": slug.current}
`)

export const supportusQuery = defineQuery(`
  *[_type == "supportus" && !(_id in path('drafts.**')) ][0]{
    _id,
    _type,
    content,
    account,
    contact
  }
`)

export const submissionGuideQuery = defineQuery(`
  *[_type == "submissionGuide" && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
    _id,
    _type,
    title,
    guides[]{
      title,
      content
    },
    "sections": sections[]{
      _type == "refSection" => {
        _type,
        title,
        "sections": sections[]->{
          title,
          contents,
          "slug": slug.current
        }
      },
      _type == "textSection" => {
        _type,
        title,
        description
      }
    },
    file{
      _key,
      asset->{
        _id,
        url,
        originalFilename,
        size
      }
    },
  }
`)

export const faqQuery = defineQuery(`
  *[_type == "faq" && !(_id in path('drafts.**')) ][0]{
    _id,
    _type,
    title,
    faqs[]{
      category,
      content
    },
    footer_content
  }
`)

export const sectionsQuery = defineQuery(`
  *[_type == "sections" && !(_id in path('drafts.**')) && publishedAt <= now() && active == true ]| order(publishedAt asc){
    _id,
    _type,
    title,
    description,
    active,
    "slug": slug.current,
    "thumbnail": *[
      _type == "programs" && !(_id in path('drafts.**')) && publishedAt <= now() &&
      references(^._id) && year_ref._ref == *[_type == "settings"][0].years._ref
    ] | order(publishedAt asc, _createdAt asc)[0].thumbnail
  }
`)

export const sectionBySlugQuery = defineQuery(`{
  "section":
  *[_type == "sections" && slug.current == $slug && !(_id in path('drafts.**')) && publishedAt <= now() && active == true][0]{
    _id,
    _type,
    title,
    contents,
    active,
    "slug": slug.current,
  },
  "programs": *[
    _type == "programs" && !(_id in path('drafts.**')) && publishedAt <= now() &&
    year_ref._ref == *[_type == "settings"][0].years._ref &&
    $slug in section[]->slug.current
  ] | order(publishedAt asc, _createdAt asc){
    _id,
    title,
    title_kr,
    thumbnail,
    "slug": slug.current,
    year,
    genre,
    section[]->{
      _id,
      title,
      "slug": slug.current,
    },
    director[]->{
      _id,
      title,
      title_kr,
    },
    color,
    subtitles,
    rating,
    runtime,
    premiere
  }
}
`)

export const programBySlugQuery = defineQuery(`
  *[_type == "programs" && slug.current == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
    _id,
    _type,
    thumbnail,
    slides,
    title,
    title_kr,
    "slug": slug.current,
    section[]->{
      _id,
      title,
      "slug": slug.current,
    },
    director[]->{
      _id,
      title,
      title_kr,
      slug,
      thumbnail,
      filmography
    },
    year,
    year_ref->{
      year,
      festival_round
    },
    genre,
    color,
    subtitles,
    premiere,
    rating,
    runtime,
    synopsis,
    trailer,
    credits,
    publishedAt,
    "schedule": *[
      _type == "screening" && !(_id in path('drafts.**')) && publishedAt <= now() &&
      references(^._id)
    ] | order(duration.start asc, publishedAt asc, _createdAt asc){
        _id,
        _type,
        title,
        "year": years->year,
        "places": places->{
          title,
          "slug": slug.current,
        },
        "place": place,
        "round": round,
        "duration": duration,
        "programs": programs[]->{
          _id,
          title,
          title_kr,
          "slug": slug.current,
          "subtitles": subtitles,
          "rating": rating,
          subtitles,
        },
        screeningEvents[]->{
          symbol,
          _id
        },
        "slug": slug.current,
      }
  }
`)

export const relatedFilmsQuery = defineQuery(`{
  "currentFilm": *[_type == "programs" && slug.current == $slug && !(_id in path('drafts.**'))][0]{
    _id,
    year_ref,
    section[]{
      _id,
      _ref
    },
    director[]{
      _id,
      _ref
    }
  },
  "sameYearSection": *[
    _type == "programs" &&
    slug.current != $slug &&
    !(_id in path('drafts.**')) &&
    publishedAt <= now()
  ]{
    _id,
    title,
    title_kr,
    thumbnail,
    "slug": slug.current,
    year,
    genre,
    section[]->{
      _id,
      title,
      "slug": slug.current,
    },
    director[]->{
      _id,
      title,
      title_kr,
    },
    color,
    subtitles,
    rating,
    runtime,
    premiere,
    year_ref,
    "sectionRefs": section[]._ref,
    "directorRefs": director[]._ref
  } | order(publishedAt desc, _createdAt desc)[0...50]
}`)

// 현재 설정된 연도(`settings.years`)와 일치하는 `places` 중에서
// 게시 시각이 가장 오래된(asc) 문서의 슬러그를 반환
export const firstPlaceSlugForCurrentYearQuery = defineQuery(`
  *[
    _type == "places" && !(_id in path('drafts.**')) && publishedAt <= now() &&
    years._ref == *[_type == "settings"][0].years._ref
  ] | order(publishedAt asc, _createdAt asc)[0].slug.current
`)

export const placesQuery = defineQuery(`
  *[_type == "places" && !(_id in path('drafts.**')) && publishedAt <= now() && years._ref == *[_type == "settings"][0].years._ref]{
    _id,
    _type,
    title,
    "slug": slug.current,
  } | order(publishedAt asc, _createdAt asc)
`)

export const placeBySlugQuery = defineQuery(`
  *[_type == "places" && slug.current == $slug && !(_id in path('drafts.**')) && publishedAt <= now()][0]{
    _id,
    _type,
    title,
    map,
    "year": years->year,
    "slug": slug.current,
    "mapUrl": mapUrl[]{
      platform,
      url
    },
    contents
  }
`)

export const screeningQuery = defineQuery(`
  {
    "screening":*[_type == "screening" && publishedAt <= now() && !(_id in path('drafts.**')) && publishedAt <= now() && years._ref == *[_type == "settings"][0].years._ref]{
      _id,
      _type,
      title,
      "year": years->year,
      "places": places->{
        title,
        "slug": slug.current,
      },
      "place": place,
      "round": round,
      "duration": duration,
      "programs": programs[]->{
        _id,
        title,
        title_kr,
        "slug": slug.current,
        "subtitles": subtitles,
        "rating": rating,
        subtitles,
      },
      screeningEvents[]->{
        symbol,
        _id
      },
      "slug": slug.current,
    },
    "events": *[_type == "screeningEvent" && !(_id in path('drafts.**')) &&active==true]{
      _id,
      _type,
      title,
      symbol,
      active,
      description,
      goodToKnow,
      "slug": slug.current,
    }
  }
`)

export const searchProgramsInSectionsQuery = defineQuery(`{
  "section":
  *[_type == "sections" && slug.current == $slug && !(_id in path('drafts.**')) && publishedAt <= now() && active == true][0]{
    _id,
    _type,
    title,
    contents,
    active,
    "slug": slug.current,
  },
  "sections": *[_type == "sections" && !(_id in path('drafts.**')) && publishedAt <= now()] | order(title asc){
    _id,
    title,
    "slug": slug.current,
  },
  "years": *[_type == "years" && !(_id in path('drafts.**')) && publishedAt <= now()] | order(festival_round desc){
    _id,
    year,
    festival_round,
    "slug": slug.current,
  },
  "total": count(
    *[
      _type == "programs" && !(_id in path('drafts.**')) && publishedAt <= now() &&
      defined($slug) && $slug in section[]->slug.current &&
      (
        !defined($sectionSlugs) || count($sectionSlugs) == 0 || count(section[]->slug.current[@ in $sectionSlugs]) > 0
      ) &&
      (
        !defined($yearSlugs) || count($yearSlugs) == 0 || year_ref->slug.current in $yearSlugs
      ) &&
      (
        !defined($q) || $q == "" || title match $q || title_kr match $q || count(director[]->[title match $q || title_kr match $q]) > 0
      ) &&
      (
        !defined($title) || $title == "" || title match $title || title_kr match $title
      ) &&
      (
        !defined($director) || $director == "" || count(director[]->[title match $director || title_kr match $director]) > 0
      )
    ]
  ),
  "items": *[
      _type == "programs" && !(_id in path('drafts.**')) && publishedAt <= now() &&
      defined($slug) && $slug in section[]->slug.current &&
      (
        !defined($sectionSlugs) || count($sectionSlugs) == 0 || count(section[]->slug.current[@ in $sectionSlugs]) > 0
      ) &&
      (
        !defined($yearSlugs) || count($yearSlugs) == 0 || year_ref->slug.current in $yearSlugs
      ) &&
      (
        !defined($q) || $q == "" || title match $q || title_kr match $q || count(director[]->[title match $q || title_kr match $q]) > 0
      ) &&
      (
        !defined($title) || $title == "" || title match $title || title_kr match $title
      ) &&
      (
        !defined($director) || $director == "" || count(director[]->[title match $director || title_kr match $director]) > 0
      )
    ] | order(publishedAt asc, _createdAt asc)[$start...$end]{
      _id,
      title,
      title_kr,
      thumbnail,
      "slug": slug.current,
      year,
      genre,
      section[]->{
        _id,
        title,
        "slug": slug.current,
      },
      director[]->{
        _id,
        title,
        title_kr,
      },
      color,
      subtitles,
      rating,
      runtime,
      premiere
    }
}`)

export const searchProgramsQuery = defineQuery(`{
  "sections": *[_type == "sections" && !(_id in path('drafts.**')) && publishedAt <= now()] | order(title asc){
    _id,
    title,
    "slug": slug.current,
  },
  "years": *[_type == "years" && !(_id in path('drafts.**')) && publishedAt <= now()] | order(festival_round desc){
    _id,
    year,
    festival_round,
    "slug": slug.current,
  },
  "total": count(
    *[
      _type == "programs" && !(_id in path('drafts.**')) && publishedAt <= now() &&
      (
        !(defined($sectionSlugs) && count($sectionSlugs) > 0)
        || count(section[@->slug.current in $sectionSlugs]) > 0
      ) &&
      (
        !(defined($yearSlugs) && count($yearSlugs) > 0)
        || year_ref->slug.current in $yearSlugs
      ) &&
      (
        !defined($q) || $q == "" || (
          title match $q || title_kr match $q || count(director[@->title match $q || @->title_kr match $q]) > 0
        )
      ) &&
      (
        !defined($title) || $title == "" || (title match $title || title_kr match $title)
      ) &&
      (
        !defined($director) || $director == "" || count(director[@->title match $director || @->title_kr match $director]) > 0
      )
    ]
  ),
  "items": *[
      _type == "programs" && !(_id in path('drafts.**')) && publishedAt <= now() &&
      (
        !(defined($sectionSlugs) && count($sectionSlugs) > 0)
        || count(section[@->slug.current in $sectionSlugs]) > 0
      ) &&
      (
        !(defined($yearSlugs) && count($yearSlugs) > 0)
        || year_ref->slug.current in $yearSlugs
      ) &&
      (
        !defined($q) || $q == "" || (
          title match $q || title_kr match $q || count(director[@->title match $q || @->title_kr match $q]) > 0
        )
      ) &&
      (
        !defined($title) || $title == "" || (title match $title || title_kr match $title)
      ) &&
      (
        !defined($director) || $director == "" || count(director[@->title match $director || @->title_kr match $director]) > 0
      )
    ] | order(publishedAt asc, _createdAt asc)[$start...$end]{
      _id,
      title,
      title_kr,
      thumbnail,
      "slug": slug.current,
      year,
      genre,
      section[]->{
        _id,
        title,
        "slug": slug.current,
      },
      director[]->{
        _id,
        title,
        title_kr,
      },
      color,
      subtitles,
      rating,
      runtime,
      premiere
    }
}`)
